import { useLocales as getLocales } from 'src/locales';

import { isEmpty } from './index';

// ----------------------------------------------------------------------

/*
 * Locales code
 * https://gist.github.com/raushankrjha/d1c7e35cf87e69aa8b4208a8171a8416
 */

type InputValue = string | number | null;

function getLocaleCode() {
  const {
    currentLang: {
      numberFormat: { code, currency },
    },
  } = getLocales();

  return {
    code: code ?? 'en-US',
    currency: currency ?? 'CNY',
  };
}

// ----------------------------------------------------------------------

export function fNumber(inputValue: InputValue) {
  const { code } = getLocaleCode();

  if (!inputValue && typeof inputValue !== 'number') return '';

  const number = Number(inputValue);

  const fm = new Intl.NumberFormat(code, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);

  return fm;
}

// ----------------------------------------------------------------------

export function fCurrency(inputValue: InputValue) {
  const { code, currency } = getLocaleCode();

  if (inputValue === null || inputValue === undefined) return '-';

  const number = Number(inputValue);

  if (Number.isNaN(number)) {
    return '-';
  }

  const fm = new Intl.NumberFormat(code, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);

  return fm;
}

// ----------------------------------------------------------------------

export function fPercent(inputValue: InputValue) {
  const { code } = getLocaleCode();

  if (!inputValue) return '';

  const number = Number(inputValue) / 100;

  const fm = new Intl.NumberFormat(code, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(number);

  return fm;
}

// ----------------------------------------------------------------------

export function fShortenNumber(inputValue: InputValue) {
  const { code } = getLocaleCode();

  if (!inputValue) return '';

  const number = Number(inputValue);

  const fm = new Intl.NumberFormat(code, {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(number);

  return fm.replace(/[A-Z]/g, (match) => match.toLowerCase());
}

// ----------------------------------------------------------------------

export function fData(inputValue: InputValue) {
  if (!inputValue) return '';

  if (inputValue === 0) return '0 Bytes';

  const units = ['bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];

  const decimal = 2;

  const baseValue = 1024;

  const number = Number(inputValue);

  const index = Math.floor(Math.log(number) / Math.log(baseValue));

  const fm = `${parseFloat((number / baseValue ** index).toFixed(decimal))} ${units[index]}`;

  return fm;
}

// ----------------------------------------------------------------------

/**
 * 根据科目的计量单位格式化数值
 * @param value 要格式化的数值
 * @param measurementUnit 计量单位 ('￥', '%', '个/件/位' 等)
 * @returns 格式化后的字符串
 */
export function formatValueByUnit(value?: number, measurementUnit?: string): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (!measurementUnit) {
    return fCurrency(value); // 默认使用货币格式
  }

  switch (measurementUnit) {
    case '￥':
      return fCurrency(value);
    case '%':
      return `${fNumber(typeof value === 'number' ? value : Number(value))}%`;
    case '个/件/位':
      return `${fNumber(value)}${measurementUnit}`;
    default:
      return `${fNumber(value)}${measurementUnit}`;
  }
}

/**
 * 格式化带颜色和符号的交易金额显示
 * @param value 金额数值
 * @param type 交易类型 ('Deposit' | 'Withdraw' | undefined)
 * @param measurementUnit 计量单位，默认为 '￥'
 * @param zeroShowcurrency 零值是否显示货币符号，默认为 false
 * @returns React 元素或字符串
 */
export function formatTransactionValue(
  value: string | null | undefined,
  type?: string,
  showSign: boolean = true,
  zeroShowcurrency: boolean = false,
  measurementUnit: string = '￥',
): string {
  if (isEmpty(value)) {
    return '-';
  }

  const numValue = Number(value);
  if (numValue === 0) {
    return zeroShowcurrency ? formatValueByUnit(0, measurementUnit) : '0';
  }

  const absValue = Math.abs(numValue);

  // 格式化数值（移除货币符号）
  const formattedValue = formatValueByUnit(absValue, measurementUnit).replace('￥', '');

  // 根据类型和数值决定显示方式
  if (type === 'Deposit' && numValue > 0) {
    // Deposit 类型且为正数：显示绿色正号
    return showSign ? `+${formattedValue}` : `${formattedValue}`;
  }

  if (type === 'Withdraw' || numValue < 0) {
    // Withdraw 类型或负数：显示橙色负号
    return `-${formattedValue}`;
  }

  // 如果没有 type 字段，根据数值本身的正负来决定
  if (numValue > 0) {
    return showSign ? `+${formattedValue}` : `${formattedValue}`;
  }

  return `${formatValueByUnit(numValue, measurementUnit).replace('￥', '')}`;
}
